import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { getPrimaryProductPhoto } from "@/lib/productPhotos";

// Live compound/accessory/bundle search. Searches product name AND CAS
// number so "GHK-Cu" and "49557-75-7" both surface the same product.
// Bundles are included via /api/bundles so 'stack' surfaces Compound
// Stacks. Results are round-robin interleaved by format so a broad query
// like 'vial' returns Raw Vials AND Vial Kits (not just the first 8 Raw
// Vials in DB order).
const MAX_RESULTS = 8;

export default function SearchBox() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  // Fetch compounds + accessories + bundles in parallel once, the catalog
  // is small enough that client-side filtering is instantaneous.
  const { data: compounds = [] } = useQuery({
    queryKey: ["search-compounds"],
    queryFn: async () => (await api.get("/products")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: accessories = [] } = useQuery({
    queryKey: ["search-accessories"],
    queryFn: async () => (await api.get("/products?format=accessory")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: bundles = [] } = useQuery({
    queryKey: ["search-bundles"],
    queryFn: async () => (await api.get("/bundles")).data,
    staleTime: 5 * 60 * 1000,
  });

  // Normalise each collection into a common shape with a route + format
  // tag so the render + click handlers don't have to branch on kind.
  const all = useMemo(() => {
    const products = [...compounds, ...accessories].map((p) => ({
      ...p,
      _route: `/product/${p.slug}`,
      _formatKey: p.is_accessory ? "accessory" : p.format || "product",
    }));
    const bundleEntries = bundles.map((b) => ({
      ...b,
      _route: `/bundle/${b.slug}`,
      _formatKey: "bundle",
      // Bundles don't expose a `format` field, synthesize one so the
      // format chip still renders sensibly on the result row.
      format: b.group ? "bundle" : "bundle",
    }));
    return [...products, ...bundleEntries];
  }, [compounds, accessories, bundles]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matches = all.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const cas = String(p.cas_number || "").toLowerCase();
      const slug = (p.slug || "").toLowerCase();
      return name.includes(q) || cas.includes(q) || slug.includes(q);
    });
    // Round-robin interleave by format so a broad query ('vial', 'pen')
    // surfaces a mix instead of the first N matches of a single format
    // starving out the rest of the cap.
    const byFormat = new Map();
    matches.forEach((m) => {
      const key = m._formatKey || "other";
      if (!byFormat.has(key)) byFormat.set(key, []);
      byFormat.get(key).push(m);
    });
    const interleaved = [];
    const buckets = Array.from(byFormat.values());
    let idx = 0;
    while (interleaved.length < MAX_RESULTS && buckets.some((b) => b.length > 0)) {
      const bucket = buckets[idx % buckets.length];
      if (bucket.length > 0) interleaved.push(bucket.shift());
      idx += 1;
    }
    return interleaved;
  }, [query, all]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onSelect = (entry) => {
    setQuery("");
    setFocused(false);
    navigate(entry._route);
  };

  const show = focused && query.trim().length > 0;

  // Log zero-result queries. Debounce for 800ms after the last keystroke,
  // require ≥3 chars (avoid noise from "a", "b" typos) and de-dupe against
  // the last logged term so a slow typer doesn't log every incremental
  // prefix. Best-effort, swallowed on error.
  const lastLoggedRef = useRef("");
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!show || q.length < 3 || results.length > 0) return;
    if (lastLoggedRef.current === q) return;
    const handle = setTimeout(() => {
      lastLoggedRef.current = q;
      api.post("/search/miss", { query: q }).catch(() => {});
    }, 800);
    return () => clearTimeout(handle);
  }, [query, results.length, show]);

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="nav-search-form">
      <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle" strokeWidth={1.5} />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          // If the user dismissed with Escape but the input still has
          // native focus, any further typing should reopen the dropdown
          // without forcing them to blur/refocus first.
          if (!focused) setFocused(true);
        }}
        onFocus={() => setFocused(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && results[0]) {
            e.preventDefault();
            onSelect(results[0]);
          }
          if (e.key === "Escape") setFocused(false);
        }}
        placeholder="Search compounds, CAS number..."
        className="w-full rounded-full border border-line bg-white py-2.5 pl-11 pr-4 text-sm outline-none transition-all focus:border-brandblue focus:ring-2 focus:ring-brandblue/20"
        data-testid="nav-search-input"
        aria-label="Search products"
      />
      {show && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl panel-warm bg-white shadow-lg"
          data-testid="search-results"
        >
          {results.length === 0 ? (
            <p className="p-4 text-sm text-subtle" data-testid="search-no-results">
              No products match &quot;{query}&quot;.
            </p>
          ) : (
            results.map((p) => {
              // Single primary image per result, deliberately static (no
              // hover-swap here, that's PDP-only). Falls back to a
              // ${format} chip alone if the SKU doesn't have a mapped
              // photo yet so the row never renders as a broken img.
              const photo = getPrimaryProductPhoto(p);
              return (
                <button
                  key={`${p._formatKey}-${p.slug}`}
                  onClick={() => onSelect(p)}
                  className="flex w-full items-center justify-between gap-3 border-b border-line px-4 py-3 text-left last:border-none hover:bg-brandblue/5"
                  data-testid={`search-result-${p.slug}`}
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-line"
                    data-testid={`search-result-thumb-${p.slug}`}
                  >
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        className="h-full w-full object-contain object-center p-1"
                        draggable={false}
                        loading="lazy"
                      />
                    ) : (
                      <span className="font-mono text-[9px] tracking-[0.14em] text-subtle">
                        {(p.format || "").slice(0, 3).toUpperCase() || "—"}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <p className="truncate font-heading text-sm font-bold text-ink">{p.name}</p>
                    {p.cas_number && (
                      <p className="mt-0.5 font-mono text-[11px] text-subtle">CAS · {p.cas_number}</p>
                    )}
                  </span>
                  <span
                    className="font-mono text-[10px] tracking-[0.14em] text-brandblue"
                    data-testid={`search-result-format-${p.slug}`}
                  >
                    {(p._formatKey || p.format || "product").toUpperCase()}
                  </span>
                </button>
              );
            })
          )}
          <Link
            to={`/peptides?q=${encodeURIComponent(query)}`}
            onClick={() => setFocused(false)}
            className="block border-t border-line bg-[#FAFBFC] px-4 py-2 text-center font-mono text-[10px] tracking-[0.18em] text-subtle hover:text-ink"
          >
            SEE ALL RESULTS →
          </Link>
        </div>
      )}
    </div>
  );
}
